/**
 * RowChips — per-position thesis state chips in the Book positions table
 * (chat-centric revamp Tasks 9/14).
 *
 * ◔ draft — a chat session holds an UNSAVED thesis draft for this symbol
 *   (any cached active session's `unsaved_artifacts`). Tapping deep-links
 *   Chat → the owning session so the draft can be finished.
 * ● covered — a saved PositionThesis exists for this position. The thesis
 *   coverage card on the dashboard only carries the aggregate count, so the
 *   row-level state comes from the same position-keyed thesis query the
 *   thesis forms and PositionDetail already use (usePositionTheses).
 * ⚠ stop-breach — the position's last price is at or below the latest
 *   thesis's stop_loss_price (theses arrive sorted updated_at DESC, so [0]
 *   is latest). A breach supersedes the covered chip: the reader needs the
 *   urgent signal, not the calm one.
 *
 * Tapping the covered/breach chip opens the position detail — the same
 * destination as the row itself; PositionTable hands its (navigation-aware)
 * row handler down. The draft chip navigates itself (Chat deep-link).
 */

import React, { useMemo } from 'react';
import { StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { useNavigation } from '@react-navigation/native';
import { useTheme } from '../../contexts/ThemeContext';
import { usePositionTheses } from '../../services/queries/usePositionTheses';
import { useActiveSessionsCached } from '../../hooks/useChatSessions';

interface RowChipsProps {
  positionId: number;
  symbol: string;
  /** Position's last price (close_price; null when stale or unknown).
   * The API serializes Decimal fields as JSON strings, so this may arrive
   * as a string — coerced with Number() before the stop-loss compare. */
  lastPrice: number | string | null;
  /** Chip tap destination — PositionTable passes its row handler. */
  onPress?: () => void;
}

export function RowChips({ positionId, symbol, lastPrice, onPress }: RowChipsProps) {
  const { theme } = useTheme();
  const { t } = useTranslation();
  const navigation = useNavigation<any>();
  const styles = useMemo(() => createThemedStyles(theme), [theme]);

  // While loading or on error the theses list is undefined → no covered/
  // breach chip, so a slow/failed fetch can never flash a misleading
  // "uncovered" state.
  const { data: theses } = usePositionTheses(positionId);
  const latest = theses?.[0];

  const covered = !!latest;
  const stopPrice = latest?.stop_loss_price ?? null;
  // Numeric compare, always: close_price is a Decimal-JSON STRING on the
  // wire, and a lexicographic "90" <= "120" is false — a silently missed
  // breach. Coerce once; NaN (null/garbage) means "no price, no breach".
  const lastNum = lastPrice == null || lastPrice === '' ? NaN : Number(lastPrice);
  const breached =
    covered && stopPrice !== null && Number.isFinite(lastNum) && lastNum <= stopPrice;

  // ◔ draft chip: the first cached active session holding an unsaved thesis
  // artifact for this symbol (uppercase compare — session targets and row
  // symbols disagree about case across sources).
  const sessions = useActiveSessionsCached();
  const draftSessionId = useMemo(() => {
    const sym = symbol.trim().toUpperCase();
    for (const s of sessions) {
      if (
        s.unsaved_artifacts?.some(
          (a) => a.kind === 'thesis' && (a.target ?? '').trim().toUpperCase() === sym
        )
      ) {
        return s.id;
      }
    }
    return null;
  }, [sessions, symbol]);

  if (!covered && draftSessionId === null) {
    return null;
  }

  return (
    <View style={styles.chipGroup}>
      {draftSessionId !== null && (
        <TouchableOpacity
          style={[styles.chip, { borderColor: theme.colors.primary }]}
          onPress={() =>
            navigation.navigate('Chat', {
              screen: 'ChatScreen',
              params: { sessionId: draftSessionId },
            })
          }
          activeOpacity={0.7}
          accessibilityRole="button"
          accessibilityLabel={t('book.chipDraftA11y', {
            defaultValue: '{{symbol}} has an unsaved thesis draft',
            symbol,
          })}
          testID={`row-chip-draft-${positionId}`}
        >
          <Text style={[styles.chipGlyph, { color: theme.colors.primary }]}>◔</Text>
        </TouchableOpacity>
      )}
      {covered && (
        <TouchableOpacity
          style={[
            styles.chip,
            { borderColor: breached ? theme.colors.error : theme.colors.success },
          ]}
          onPress={onPress}
          activeOpacity={0.7}
          accessibilityRole="button"
          accessibilityLabel={
            breached
              ? t('book.chipBreachA11y', {
                  defaultValue: '{{symbol}} last price is at or below its stop-loss',
                  symbol,
                })
              : t('book.chipCoveredA11y', {
                  defaultValue: '{{symbol}} has a saved thesis',
                  symbol,
                })
          }
          testID={breached ? `row-chip-breach-${positionId}` : `row-chip-covered-${positionId}`}
        >
          <Text
            style={[
              styles.chipGlyph,
              { color: breached ? theme.colors.error : theme.colors.success },
            ]}
          >
            {breached ? '⚠' : '●'}
          </Text>
        </TouchableOpacity>
      )}
    </View>
  );
}

function createThemedStyles(theme: any) {
  return StyleSheet.create({
    chipGroup: {
      flexDirection: 'row',
      marginHorizontal: theme.spacing.xxs,
    },
    chip: {
      width: 18,
      height: 18,
      borderRadius: theme.radii.lg,
      borderWidth: theme.id === 'bauhaus' ? 2 : 1,
      alignItems: 'center',
      justifyContent: 'center',
    },
    chipGlyph: {
      fontSize: 10,
      lineHeight: 12,
    },
  });
}
